import { computed, inject, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIChatMessageData } from '../utils/types'
import { TagRef, TagSuggestionWithOrigin } from '@/entities/tags/utils/types'
import { AIType } from '../utils/enums'

import { generateId } from '@/entities/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { createSuggestionTimeout } from '../utils/timeout'
import { noSentenceRequest, prepareMessages } from '../utils/helpers'
import { readTagSuggestions } from './utils/helpers'
import { useAIPortfolioContext } from '../utils/context'

import { useUserStore } from '@/store/user'

/** As many as the prompt is allowed to return. Mirrors TAG_SUGGESTION_MAX. */
const MAX_SUGGESTIONS = 2

/**
 * The assistant behind the wizard's suggested tags.
 *
 * The third of the suggestion assistants, and the one that answers with a list:
 * an asset carries several tags across several dimensions, so "one pick or none"
 * would be the wrong shape. Some of what it suggests already exists in the
 * repository and some does not — which is which is decided here, by matching
 * against the tags that do, never by asking the model.
 *
 * Silent to the user on every failure, reported through handleCatchedError so
 * Faro sees it: suggested tags are an aid, and their absence is not something
 * the user can act on.
 */
export const useAITagSuggestionStore = defineStore('ai-tag-suggestion', () => {
  // INIT
  const userStore = useUserStore()

  const { tagsList } = useAIPortfolioContext()

  const ws = inject<WSInstance>('ws')

  const requestId = ref(`tmp_${generateId()}`)

  const suggestions = ref<TagSuggestionWithOrigin[]>([])
  const loading = ref(false)

  /**
   * Whether tags have been asked for at all, regardless of the answer. The
   * wizard no longer asks on its own — the user asks by clicking "Ask AI" —
   * so this is what tells that control to stop offering itself once it has
   * done its job.
   */
  const asked = ref(false)

  /*
   * The repository's tags and the applied ones, as the live request saw them.
   * Held because the answer is read against them: what exists decides which
   * suggestions are new, and what is applied decides which are worth showing.
   */
  let existingTags: TagRef[] = []
  let appliedTags: TagRef[] = []

  /** What has already been asked, so re-entering the step does not re-ask. */
  let askedFor = ''

  /*
   * A websocket that goes quiet leaves `loading` true, and the wait is drawn on
   * screen — a pulse that never resolves reads as the assistant still thinking
   * about a question nobody is going to answer.
   */
  const timeout = createSuggestionTimeout()

  // GETTERS

  const getSuggestions = computed(() => suggestions.value)

  const isLoading = computed(() => loading.value)

  const hasAsked = computed(() => asked.value)

  // ACTIONS

  const reset = () => {
    requestId.value = `tmp_${generateId()}`
    suggestions.value = []
    timeout.stop()
    loading.value = false
    asked.value = false
    existingTags = []
    appliedTags = []
    askedFor = ''
  }

  /**
   * Asks how this holding should be tagged.
   *
   * Asked with or without a sentence. With one, the words are the request; with
   * none — a wizard opened straight from the Add asset dialog — the request
   * says so, and the prompt suggests from the flow and this repository's own
   * vocabulary instead, grounding each reason in that rather than in words
   * nobody typed. The caller decides when to ask; see Wizard.vue, which asks
   * only once the user clicks "Ask AI" on the chips row.
   */
  const suggest = async (params: {
    sentence?: string
    flow: string
    existing: TagRef[]
    applied: TagRef[]
  }) => {
    const { sentence, flow, existing, applied } = params

    // The websocket is absent when VITE_APP_WS_DISABLED is set.
    if (!ws) return

    /*
     * Keyed on the sentence and the flow only. The applied tags change as the
     * user accepts suggestions, and re-asking on every acceptance would spend a
     * call to be told the same thing minus one — the answer is filtered against
     * the applied list when it arrives instead.
     */
    const request = sentence?.trim() || noSentenceRequest(flow)

    const key = `${flow}|${request}`
    if (key === askedFor) return

    askedFor = key
    asked.value = true
    requestId.value = `tmp_${generateId()}`
    existingTags = [...existing]
    appliedTags = [...applied]
    suggestions.value = []
    loading.value = true

    /*
     * Asking again is allowed after a timeout — the question was never
     * answered, so the key is dropped with the wait.
     */
    timeout.start(() => {
      loading.value = false
      askedFor = ''
      handleCatchedError('Tag suggestion timed out — no answer for tags')
    })

    const { messages } = prepareMessages(request)

    const messageData: AIChatMessageData = {
      ai_type: AIType.TAG_SUGGESTION,
      messages,
      dynamic_context: {
        flow,
        // The repository's own vocabulary, quoted as every prompt quotes it.
        tags_list: tagsList.value,
        applied_tags: applied.length
          ? applied.map(tag => `{ tag_name: "${tag.tag_name}", tag_value: "${tag.tag_value}" }`).join(', ')
          : 'none yet',
        max_suggestions: MAX_SUGGESTIONS,
      },
    }

    ws.send('ai', 'chat', requestId.value, messageData)
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        (message: WSResponse<string>, responseId: string) => {
          // Every assistant shares this channel; the request id is what says
          // the answer is ours.
          if (responseId !== requestId.value) return

          try {
            if (message.errors || !message.data) {
              handleCatchedError(
                `Tag suggestions failed: ${message.errors?.join(', ') ?? 'empty answer'}`,
              )
              return
            }

            const result = readTagSuggestions(message.data, existingTags, appliedTags)

            // Each dropped suggestion is reported; the rest are still shown.
            for (const error of result.errors) handleCatchedError(error)

            suggestions.value = result.suggestions
          } catch (e) {
            handleCatchedError(e as string)
          } finally {
            timeout.stop()
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  return {
    isLoading,
    hasAsked,

    getSuggestions,

    suggest,
    reset,
  }
})
